// Export- und Beschriftungslogik fuer Spielanmeldungen.
// Liegt hier, damit die Spieleliste und die Detailseite dieselbe Ausgabe
// erzeugen. drinkTypes kommt aus der Getraenke-Verwaltung und wird
// hereingereicht, weil es im Backend gepflegt wird.
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

export const getTicketTypeLabel = (type) => {
    const types = {
      'stehplatz': 'Stehplatz',
      'sitzplatz': 'Sitzplatz',
      'ohne-ticket': 'Ohne Ticket'
    };
    return types[type] || type;
  };

export const getDrinkLabel = (drinks, drinkTypes = []) => {
    if (!drinks) return 'Nicht angegeben';

    // Wenn es ein String ist (komma-separiert), zu Array konvertieren
    const drinksArray = typeof drinks === 'string'
      ? drinks.split(',').filter(d => d.trim())
      : (Array.isArray(drinks) ? drinks : []);

    if (drinksArray.length === 0) return 'Nicht angegeben';

    // Labels für jedes Getränk holen
    const labels = drinksArray.map(drinkValue => {
      const drinkType = drinkTypes.find(d => d.value === drinkValue.trim());
      return drinkType ? drinkType.label : drinkValue;
    });

    return labels.join(', ');
  };

export const generatePDF = (game, drinkTypes = []) => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    const isChampionsLeague = game.category === 'champions-league';

    doc.setFontSize(20);
    doc.text('EFC Buseck - Anmeldungsliste', 20, 30);

    doc.setFontSize(14);
    doc.text(`Spiel: ${game.opponent}`, 20, 50);
    doc.text(`Datum: ${new Date(game.date).toLocaleDateString('de-DE')} - ${game.time}`, 20, 65);
    doc.text(`Ort: ${game.location}`, 20, 80);
    doc.text(`Anmeldungen: ${game.registrations.length}`, 20, 95);

    if (isChampionsLeague) {
      doc.setFontSize(10);
      doc.setTextColor(255, 140, 0);
      doc.text('Champions League - Erweiterte Teilnehmerdaten', 20, 107);
      doc.setTextColor(0, 0, 0);
    }

    // Für Champions League: Alle Daten in einer Tabelle
    if (isChampionsLeague) {
      const tableData = game.registrations.map((reg, index) => [
        index + 1,
        `${reg.firstName || ''} ${reg.lastName || ''}`.trim() || reg.name,
        getTicketTypeLabel(reg.ticketType),
        reg.dateOfBirth ? new Date(reg.dateOfBirth).toLocaleDateString('de-DE') : '-',
        reg.placeOfBirth || '-',
        reg.address || '-',
        reg.idCardNumber || '-'
      ]);

      autoTable(doc, {
        head: [['#', 'Name', 'Ticket', 'Geb.Datum', 'Geburtsort', 'Adresse', 'Perso-Nr']],
        body: tableData,
        startY: 115,
        theme: 'grid',
        styles: {
          fontSize: 7,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [220, 20, 60],
          fontSize: 8,
          fontStyle: 'bold'
        },
        margin: { left: 10, right: 10 },
        tableWidth: 'auto'
      });
    } else {
      // Standard-Tabelle für normale Spiele
      const tableData = game.registrations.map((reg, index) => [
        index + 1,
        reg.name,
        reg.email,
        getTicketTypeLabel(reg.ticketType),
        reg.message || '-',
        new Date(reg.registeredAt).toLocaleDateString('de-DE')
      ]);

      autoTable(doc, {
        head: [['#', 'Name', 'E-Mail', 'Ticket-Art', 'Nachricht', 'Angemeldet am']],
        body: tableData,
        startY: 110,
        theme: 'grid',
        styles: { fontSize: 10 },
        headStyles: { fillColor: [66, 139, 202] }
      });
    }

    doc.save(`Anmeldungsliste_${game.opponent}_${new Date(game.date).toLocaleDateString('de-DE')}.pdf`);
  };

export const generateExcel = (game, drinkTypes = []) => {
    // Gruppiere Registrierungen nach user_id für Gruppenanmeldungen
    const groupedRegistrations = game.registrations.reduce((groups, registration) => {
      const userId = registration.userId || registration.user_id;
      if (!groups[userId]) {
        groups[userId] = [];
      }
      groups[userId].push(registration);
      return groups;
    }, {});

    let rowIndex = 0;
    const excelData = [];

    Object.values(groupedRegistrations).forEach((userRegistrations) => {
      const isGroup = userRegistrations.length > 1;

      userRegistrations.forEach((registration, groupIndex) => {
        rowIndex++;

        // Versuche firstName/lastName zu nutzen, falls vorhanden, sonst den kombinierten Namen aufteilen
        let firstName = registration.firstName || '';
        let lastName = registration.lastName || '';

        if (!firstName && !lastName && registration.name) {
          const nameParts = registration.name.split(' ');
          if (nameParts.length > 1) {
            firstName = nameParts.slice(0, -1).join(' ');
            lastName = nameParts[nameParts.length - 1];
          } else {
            firstName = registration.name;
          }
        }

        const rowData = {
          'Nr.': rowIndex,
          'Gruppe': isGroup ? `${groupIndex + 1}/${userRegistrations.length}` : '-',
          'Vorname': firstName,
          'Nachname': lastName,
          'E-Mail': registration.email,
          'Ticket-Art': getTicketTypeLabel(registration.ticketType),
          'Getränke': getDrinkLabel(registration.drink || '', drinkTypes),
          'Status': registration.status === 'pending' ? 'Ausstehend' :
                   registration.status === 'confirmed' ? 'Bestätigt' :
                   registration.status === 'cancelled'
                     ? (registration.cancelledFrom ? 'Storniert' : 'Abgelehnt')
                     : registration.status,
          'Angemeldet am': registration.registeredAt ? new Date(registration.registeredAt).toLocaleDateString('de-DE') : '-',
          'Storniert am': registration.cancelledAt ? new Date(registration.cancelledAt).toLocaleDateString('de-DE') : '-'
        };

        // Champions League Zusatzfelder
        if (game.category === 'champions-league') {
          rowData['Adresse'] = registration.address || '-';
          rowData['Perso-Nr'] = registration.idCardNumber || '-';
          rowData['Geb. Datum'] = registration.dateOfBirth ? new Date(registration.dateOfBirth).toLocaleDateString('de-DE') : '-';
          rowData['Geburtsort'] = registration.placeOfBirth || '-';
        }

        excelData.push(rowData);
      });
    });

    // Getränke-Statistik erstellen
    const drinkCounts = {};
    game.registrations.forEach(registration => {
      const drinks = registration.drink ?
        (typeof registration.drink === 'string' ? registration.drink.split(',') : [registration.drink])
        : [];
      drinks.forEach(drink => {
        const drinkLabel = getDrinkLabel(drink.trim(), drinkTypes);
        drinkCounts[drinkLabel] = (drinkCounts[drinkLabel] || 0) + 1;
      });
    });

    // ALLES IN EIN SHEET: Zusammenfassung oben, dann Anmeldungen darunter
    const isChampionsLeague = game.category === 'champions-league';
    const emptyRow = isChampionsLeague
      ? { 'A': '', 'B': '', 'C': '', 'D': '', 'E': '', 'F': '', 'G': '', 'H': '', 'I': '', 'J': '', 'K': '', 'L': '', 'M': '', 'N': '' }
      : { 'A': '', 'B': '', 'C': '', 'D': '', 'E': '', 'F': '', 'G': '', 'H': '', 'I': '', 'J': '' };

    const combinedData = [
      // ZUSAMMENFASSUNG
      isChampionsLeague
        ? { 'A': 'ZUSAMMENFASSUNG', 'B': '', 'C': '', 'D': '', 'E': '', 'F': '', 'G': '', 'H': '', 'I': '', 'J': '', 'K': '', 'L': '', 'M': '', 'N': '' }
        : { 'A': 'ZUSAMMENFASSUNG', 'B': '', 'C': '', 'D': '', 'E': '', 'F': '', 'G': '', 'H': '', 'I': '', 'J': '' },
      emptyRow,
      { 'A': 'Spiel:', 'B': game.opponent },
      { 'A': 'Datum:', 'B': `${new Date(game.date).toLocaleDateString('de-DE')} - ${game.time}` },
      { 'A': 'Anmeldungen:', 'B': `${game.registrations.length}` },
      emptyRow,
      { 'A': 'Status:', 'B': '' },
      { 'A': '  Bestätigt:', 'B': game.registrations.filter(r => r.status === 'confirmed').length },
      { 'A': '  Ausstehend:', 'B': game.registrations.filter(r => r.status === 'pending').length },
      emptyRow,
      { 'A': 'Tickets:', 'B': '' },
      { 'A': '  Stehplätze:', 'B': game.registrations.filter(r => r.ticketType === 'stehplatz').length },
      { 'A': '  Sitzplätze:', 'B': game.registrations.filter(r => r.ticketType === 'sitzplatz').length },
      { 'A': '  Ohne Ticket:', 'B': game.registrations.filter(r => r.ticketType === 'ohne-ticket').length },
      emptyRow,
      { 'A': 'Getränke:', 'B': '' },
      ...Object.entries(drinkCounts).map(([drink, count]) => ({
        'A': `  ${drink}:`,
        'B': count
      })),
      emptyRow,
      emptyRow,
      // ANMELDUNGEN HEADER
      isChampionsLeague
        ? { 'A': 'Nr.', 'B': 'Gruppe', 'C': 'Vorname', 'D': 'Nachname', 'E': 'E-Mail', 'F': 'Ticket-Art', 'G': 'Getränke', 'H': 'Status', 'I': 'Angemeldet am', 'J': 'Storniert am', 'K': 'Adresse', 'L': 'Perso-Nr', 'M': 'Geb. Datum', 'N': 'Geburtsort' }
        : { 'A': 'Nr.', 'B': 'Gruppe', 'C': 'Vorname', 'D': 'Nachname', 'E': 'E-Mail', 'F': 'Ticket-Art', 'G': 'Getränke', 'H': 'Status', 'I': 'Angemeldet am', 'J': 'Storniert am' },
      // ANMELDUNGEN DATEN
      ...excelData.map(row => {
        const baseData = {
          'A': row['Nr.'],
          'B': row['Gruppe'],
          'C': row['Vorname'],
          'D': row['Nachname'],
          'E': row['E-Mail'],
          'F': row['Ticket-Art'],
          'G': row['Getränke'],
          'H': row['Status'],
          'I': row['Angemeldet am'],
          'J': row['Storniert am'] || '-'
        };

        if (isChampionsLeague) {
          baseData['K'] = row['Adresse'] || '-';
          baseData['L'] = row['Perso-Nr'] || '-';
          baseData['M'] = row['Geb. Datum'] || '-';
          baseData['N'] = row['Geburtsort'] || '-';
        }

        return baseData;
      })
    ];

    // Workbook und Sheet erstellen
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(combinedData, { skipHeader: true });

    // Column Widths setzen
    const columnWidths = [
      { wch: 20 }, // A: Nr. / Info
      { wch: 15 }, // B: Gruppe / Wert
      { wch: 20 }, // C: Vorname
      { wch: 20 }, // D: Nachname
      { wch: 35 }, // E: E-Mail
      { wch: 15 }, // F: Ticket-Art
      { wch: 25 }, // G: Getränke
      { wch: 12 }, // H: Status
      { wch: 15 }  // I: Angemeldet am
    ];

    if (isChampionsLeague) {
      columnWidths.push(
        { wch: 40 }, // J: Adresse
        { wch: 15 }, // K: Perso-Nr
        { wch: 12 }, // L: Geb. Datum
        { wch: 20 }  // M: Geburtsort
      );
    }

    worksheet['!cols'] = columnWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Anmeldungsliste');

    // Datei speichern
    const fileName = `Anmeldungsliste_${game.opponent}_${new Date(game.date).toLocaleDateString('de-DE')}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    toast.success('Anmeldungsliste wurde als Excel exportiert!');
  };
